import { useMemo } from "react";
import type { DatumId } from "@nivo/pie";
import type { SecondaryAxisConfig } from "@/react/components/Analytics/components/LineChartComponent/types";
import {
  computeYMinMax,
  formatLineChartData,
} from "@/react/components/Analytics/components/LineChartComponent/utils";
import type { ChartDataItem } from "@/react/components/Analytics/components/types";
import { createChartGradients } from "@/react/components/Analytics/components/utils/chartGradients";
import { useTickValues } from "@/react/components/Analytics/shared/hooks/useTickValues";
import { useYTicks } from "@/react/components/Analytics/shared/hooks/useYTicks";

interface UseLineChartComputedArgs {
  activeId: DatumId | null;
  colors: string[];
  containerHeight?: number;
  containerWidth: number;
  data: ChartDataItem[];
  indexBy: string;
  marginBottom: number;
  marginTop: number;
  secondaryAxis?: SecondaryAxisConfig;
  seriesKeys: string[];
}

export interface UseLineChartComputedResult {
  activeColors: string[];
  fillPattern: ReadonlyArray<{
    id: string;
    match: Record<string, unknown> | "*";
  }>;
  formattedData: Array<{
    id: string;
    data: Array<{ x: string | number; y: number }>;
  }>;
  gradientDefs: ReadonlyArray<{ id: string; [key: string]: unknown }>;
  isSingleDataPoint: boolean;
  legendData: Array<{ id: string; color: string; label: string }>;
  normalizedHeight?: number;
  seriesColorById: Record<string, string>;
  tickValues: Array<string | number>;
  yMaxForNormalize: number | null;
  yMaxScale: number | undefined;
  yMinScale: number | undefined;
  yTicks: number[];
}

export const useLineChartComputed = ({
  data,
  seriesKeys,
  indexBy,
  colors,
  activeId,
  containerWidth,
  containerHeight,
  secondaryAxis,
  marginTop,
  marginBottom,
}: UseLineChartComputedArgs): UseLineChartComputedResult => {
  const activeColors = useMemo(
    () =>
      seriesKeys.map((key, index) =>
        activeId && activeId !== key ? `${colors[index]}80` : colors[index],
      ),
    [seriesKeys, colors, activeId],
  );

  const seriesColorById: Record<string, string> = useMemo(
    () =>
      seriesKeys.reduce(
        (acc, key, index) => {
          acc[key] = activeColors[index];
          return acc;
        },
        {} as Record<string, string>,
      ),
    [seriesKeys, activeColors],
  );

  const { gradientDefs, fillPattern } = useMemo(
    () =>
      createChartGradients({
        type: "area",
        seriesKeys,
        colors,
        activeId,
      }),
    [seriesKeys, colors, activeId],
  );

  const tickValues = useTickValues({ data, containerWidth, indexBy });

  const formattedPrimaryData = useMemo(
    () => formatLineChartData(data, seriesKeys, indexBy),
    [data, seriesKeys, indexBy],
  );

  const secondaryKeys = (secondaryAxis && secondaryAxis.keys) || [];

  const primaryKeysForNormalize = useMemo(
    () =>
      secondaryKeys.length
        ? seriesKeys.filter(k => !secondaryKeys.includes(k))
        : seriesKeys,
    [seriesKeys, secondaryKeys],
  );

  const yMaxForNormalize = useMemo(() => {
    if (!secondaryKeys.length) return null;
    return (
      data.reduce((maxAcc, item) => {
        const rowMax = primaryKeysForNormalize.reduce((acc, key) => {
          const v = Number((item as any)[key] || 0);
          return v > acc ? v : acc;
        }, 0);
        return rowMax > maxAcc ? rowMax : maxAcc;
      }, 0) || 1
    );
  }, [data, primaryKeysForNormalize, secondaryKeys.length]);

  const formattedData = useMemo(() => {
    if (!secondaryKeys.length || !yMaxForNormalize) return formattedPrimaryData;
    return seriesKeys.map(seriesKey => ({
      id: seriesKey,
      data: data.map(item => {
        const x = (item as any)[indexBy];
        const raw = Number((item as any)[seriesKey] || 0);
        const y = secondaryKeys.includes(seriesKey)
          ? (raw / 100) * yMaxForNormalize
          : raw;
        return { x, y };
      }),
    }));
  }, [
    yMaxForNormalize,
    seriesKeys,
    data,
    indexBy,
    secondaryKeys,
    formattedPrimaryData,
  ]);

  const [yMin, yMax] = useMemo(
    () => computeYMinMax(formattedData),
    [formattedData],
  );

  const yTicks = useYTicks({
    min: yMin,
    max: yMax,
    minTickCount: 4,
    maxTickCount: 7,
  });

  const yMinScale = useMemo(() => {
    if (!yTicks.length) return undefined;
    const first = yTicks[0];
    if (first >= 0) return undefined;
    const step =
      yTicks.length > 1 ? yTicks[1] - yTicks[0] : Math.abs(first) || 1;
    return first - step * 0.25;
  }, [yTicks]);

  const yMaxScale = useMemo(() => {
    if (secondaryKeys.length) return undefined;
    if (!yTicks.length) return undefined;
    return yTicks[yTicks.length - 1];
  }, [secondaryKeys.length, yTicks]);

  const legendData = useMemo(
    () =>
      seriesKeys.map((key, index) => ({
        id: key,
        color: colors[index],
        label: key,
      })),
    [seriesKeys, colors],
  );

  const isSingleDataPoint = useMemo(() => {
    const firstValue = data[0]?.[indexBy];
    return data.every(item => item[indexBy] === firstValue);
  }, [data, indexBy]);

  const normalizedHeight = useMemo(() => {
    if (!containerHeight) return undefined;
    const gaps = Math.max((yTicks?.length || 0) - 1, 1);
    const innerBase = Math.max(containerHeight - (marginTop + marginBottom), 1);
    const normalizedInner = Math.max(Math.round(innerBase / gaps) * gaps, gaps);
    return normalizedInner + marginTop + marginBottom;
  }, [containerHeight, yTicks?.length, marginTop, marginBottom]);

  return {
    activeColors,
    seriesColorById,
    gradientDefs,
    fillPattern,
    tickValues,
    formattedData,
    yTicks,
    yMinScale,
    yMaxScale,
    yMaxForNormalize,
    legendData,
    isSingleDataPoint,
    normalizedHeight,
  };
};
